import React, { useEffect, useMemo, useRef, useState } from "react";

export const GUIDE_VISUAL_STATES = {
  DEFAULT: {
    hex: "#B8E2D8",
    rimHex: "#F7FFF4",
    speed: 2,
    amplitude: 0.032,
    frequency: 0.82,
    radius: 0.7,
    height: 0.7,
    depth: 0.66,
    rimPower: 1.5,
    rimEmitBoost: 1,
    alpha: 0.74,
    internalLevel: 0.1,
    volumeSpeed: 0.5,
    volumeAmplitude: 0.07,
    volumeFrequency: 0.85,
    volumeRadius: 0.015,
    volumeHeight: 0.015,
  },
  TALKING: {
    hex: "#65DDEB",
    speed: 5,
    amplitude: 0.05,
    frequency: 1.8,
    radius: 0.9,
    volumeSpeed: 1.15,
    volumeAmplitude: 0.5
  },
  LISTENING: {
    hex: "#F263B8",
    speed: 5,
    amplitude: 0.05,
    frequency: 2.14,
    radius: 0.9,
  },
  THINKING: {
    hex: "#9D7BFD",
    speed: 15,
    amplitude: 0.09,
    frequency: 2.02,
    radius: 0.55,
  },
  WAITING: {
    hex: "#9ec2bc",
    speed: 3,
    amplitude: 0.08,
    frequency: 0.78,
    radius: 0.8,
  },
};

const VERTEX_SHADER = `
  precision highp float;

  attribute vec3 aPosition;
  attribute vec3 aNormal;

  varying vec3 vNormal;
  varying vec3 vPosition;
  varying float vWave;
  varying float vBlob;

  uniform float uTime;
  uniform vec3 uSize;
  uniform float uDefAmplitude;
  uniform float uDefFrequency;
  uniform float uDefBlend;
  uniform float uPhase;
  uniform float uAudioLevel;

  const float TAU = 6.283185307179586;

  float signal(float x, float frequency, float phase) {
    return sin(x * TAU * frequency + phase);
  }

  vec3 triPlanarVector(vec3 p, float frequency, float phase) {
    return vec3(
      signal(p.x + p.y * 0.16, frequency, phase),
      signal(p.y - p.z * 0.12, frequency * 0.94, phase * 1.17 + 1.1),
      signal(p.z + p.x * 0.14, frequency * 1.08, -phase * 0.82 + 2.2)
    ) * 0.57735;
  }

  vec3 secondaryVector(vec3 p, float frequency, float phase) {
    return vec3(
      signal(dot(p, normalize(vec3(0.74, 0.42, -0.52))), frequency, phase * 0.74),
      signal(dot(p, normalize(vec3(-0.26, 0.9, 0.36))), frequency * 0.86, -phase * 0.58 + 0.9),
      signal(dot(p, normalize(vec3(0.48, -0.34, 0.81))), frequency * 1.12, phase * 0.63 + 1.7)
    ) * 0.57735;
  }

  float scalarBlob(vec3 p, float phase) {
    float a = signal(dot(p, normalize(vec3(0.3, 0.86, 0.42))), 0.46, phase * 0.38);
    float b = signal(dot(p, normalize(vec3(-0.78, 0.18, 0.6))), 0.62, -phase * 0.27 + 1.5);
    float c = signal(dot(p, normalize(vec3(0.56, -0.58, 0.59))), 0.54, phase * 0.31 + 2.4);
    return (a + b + c) / 3.0;
  }

  vec3 displacedPoint(vec3 p) {
    vec3 n = normalize(p);
    float phase = uPhase;
    float audioPush = smoothstep(0.0, 1.0, uAudioLevel);
    float baselineAmplitude = 0.024 + uDefAmplitude * 0.2;
    float activeAmplitude = uDefAmplitude * uDefBlend * (0.46 + audioPush * 0.86);
    float frequency = max(0.22, uDefFrequency);

    vec3 low = triPlanarVector(p, 0.48, phase * 0.24);
    vec3 primary = triPlanarVector(p + low * 0.2, frequency, phase);
    vec3 secondary = secondaryVector(p + primary * 0.1, frequency * 0.72, phase + 0.8);
    float blob = scalarBlob(p + low * 0.18, phase);

    vec3 vectorOffset = low * baselineAmplitude;
    vectorOffset += primary * activeAmplitude * 0.38;
    vectorOffset += secondary * activeAmplitude * 0.2;

    float normalOffset = dot(primary, n) * activeAmplitude * 0.72;
    normalOffset += dot(secondary, n) * activeAmplitude * 0.25;
    normalOffset += blob * baselineAmplitude * 1.05;

    return p + vectorOffset + n * normalOffset;
  }

  vec3 normalFor(vec3 p, vec3 normalHint) {
    vec3 n = normalize(normalHint);
    float basisSign = n.z >= 0.0 ? 1.0 : -1.0;
    float basisScale = -1.0 / (basisSign + n.z);
    float basisCross = n.x * n.y * basisScale;
    vec3 tangent = normalize(vec3(
      1.0 + basisSign * n.x * n.x * basisScale,
      basisSign * basisCross,
      -basisSign * n.x
    ));
    vec3 bitangent = normalize(vec3(
      basisCross,
      basisSign + n.y * n.y * basisScale,
      -n.y
    ));
    float eps = 0.026;

    vec3 center = displacedPoint(p);
    vec3 tangentPoint = displacedPoint(normalize(p + tangent * eps));
    vec3 bitangentPoint = displacedPoint(normalize(p + bitangent * eps));
    vec3 result = normalize(cross(tangentPoint - center, bitangentPoint - center));

    if (dot(result, center) < 0.0) {
      result = -result;
    }

    return result;
  }

  void main() {
    vec3 p = aPosition;
    float idleDrift = uTime * 0.11;
    mat2 yaw = mat2(cos(idleDrift), -sin(idleDrift), sin(idleDrift), cos(idleDrift));
    p.xz = yaw * p.xz;

    vec3 displaced = displacedPoint(p);
    vec3 normal = normalFor(p, aNormal);
    vec3 scaled = displaced * uSize;

    vNormal = normalize(normal / max(uSize, vec3(0.001)));
    vPosition = scaled;
    float phase = uPhase;
    vec3 sheenField = secondaryVector(
      p + triPlanarVector(p, 0.42, phase * 0.2) * 0.25,
      max(0.22, uDefFrequency) * 0.72,
      phase * 0.86 + 0.7
    );
    vWave = dot(sheenField, normalize(p)) * 0.72 + scalarBlob(p, phase * 0.58 + 1.2) * 0.28;
    vBlob = scalarBlob(p, phase);

    gl_Position = vec4(scaled.x, scaled.y - 0.025, -scaled.z * 0.16, 1.0);
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;

  varying vec3 vNormal;
  varying vec3 vPosition;
  varying float vWave;
  varying float vBlob;

  uniform vec3 uBaseColor;
  uniform vec3 uRimColor;
  uniform float uRimPower;
  uniform float uRimEmitBoost;
  uniform float uAudioLevel;
  uniform float uAlpha;

  void main() {
    vec3 normal = normalize(vNormal);
    vec3 viewDir = normalize(vec3(0.0, 0.0, 2.7) - vPosition);
    vec3 lightDir = normalize(vec3(-0.38, 0.56, 0.74));
    vec3 fillDir = normalize(vec3(0.45, -0.2, 0.82));

    float facing = clamp(dot(normal, viewDir), 0.0, 1.0);
    float lambert = max(dot(normal, lightDir), 0.0);
    float fill = max(dot(normal, fillDir), 0.0);
    float rim = pow(1.0 - facing, uRimPower);
    float specular = pow(max(dot(reflect(-lightDir, normal), viewDir), 0.0), 26.0);
    float wave = vWave * 0.5 + 0.5;
    float blob = vBlob * 0.5 + 0.5;
    float activeSheen = smoothstep(0.44, 0.9, wave) * (0.028 + uAudioLevel * 0.075);

    vec3 milky = mix(uBaseColor, vec3(1.0), 0.34 + uAudioLevel * 0.08);
    vec3 color = milky * (0.44 + lambert * 0.36 + fill * 0.16);
    color += uBaseColor * blob * (0.045 + uAudioLevel * 0.045);
    color += mix(uBaseColor, uRimColor, 0.54) * activeSheen;
    color = mix(color, uRimColor, rim * (0.28 + uRimEmitBoost * 0.08));
    color += uRimColor * rim * (0.24 + uRimEmitBoost * 0.32);
    color += vec3(1.0) * specular * (0.11 + uAudioLevel * 0.08);

    float alpha = max(uAlpha, 0.88) * (0.9 + facing * 0.08 + rim * 0.08);
    alpha = clamp(alpha, 0.0, 0.98);

    gl_FragColor = vec4(color, alpha);
  }
`;

const ORB_ICOSPHERE_SUBDIVISIONS = 3;

const normalizeVector = ([x, y, z]) => {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
};

const createSphereGeometry = (subdivisions = ORB_ICOSPHERE_SUBDIVISIONS) => {
  const t = (1 + Math.sqrt(5)) / 2;
  const vertices = [
    [-1, t, 0],
    [1, t, 0],
    [-1, -t, 0],
    [1, -t, 0],
    [0, -1, t],
    [0, 1, t],
    [0, -1, -t],
    [0, 1, -t],
    [t, 0, -1],
    [t, 0, 1],
    [-t, 0, -1],
    [-t, 0, 1],
  ].map(normalizeVector);

  let faces = [
    [0, 11, 5],
    [0, 5, 1],
    [0, 1, 7],
    [0, 7, 10],
    [0, 10, 11],
    [1, 5, 9],
    [5, 11, 4],
    [11, 10, 2],
    [10, 7, 6],
    [7, 1, 8],
    [3, 9, 4],
    [3, 4, 2],
    [3, 2, 6],
    [3, 6, 8],
    [3, 8, 9],
    [4, 9, 5],
    [2, 4, 11],
    [6, 2, 10],
    [8, 6, 7],
    [9, 8, 1],
  ];

  const getMidpointIndex = (a, b, midpointCache) => {
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    const cachedIndex = midpointCache.get(key);
    if (typeof cachedIndex === "number") return cachedIndex;

    const midpoint = normalizeVector([
      (vertices[a][0] + vertices[b][0]) * 0.5,
      (vertices[a][1] + vertices[b][1]) * 0.5,
      (vertices[a][2] + vertices[b][2]) * 0.5,
    ]);
    const nextIndex = vertices.length;
    vertices.push(midpoint);
    midpointCache.set(key, nextIndex);
    return nextIndex;
  };

  for (let level = 0; level < subdivisions; level += 1) {
    const midpointCache = new Map();
    const nextFaces = [];

    faces.forEach(([a, b, c]) => {
      const ab = getMidpointIndex(a, b, midpointCache);
      const bc = getMidpointIndex(b, c, midpointCache);
      const ca = getMidpointIndex(c, a, midpointCache);

      nextFaces.push([a, ab, ca]);
      nextFaces.push([b, bc, ab]);
      nextFaces.push([c, ca, bc]);
      nextFaces.push([ab, bc, ca]);
    });

    faces = nextFaces;
  }

  const positions = [];
  const normals = [];
  const indices = [];

  vertices.forEach(([x, y, z]) => {
    positions.push(x, y, z);
    normals.push(x, y, z);
  });

  faces.forEach(([a, b, c]) => {
    indices.push(a, b, c);
  });

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    indices: new Uint16Array(indices),
    indexCount: indices.length,
  };
};

const ORB_GEOMETRY = createSphereGeometry();

export const hexToRgbGuide = (hex) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

const getPositiveAudioLevel = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, number);
};

const hexToUnitRgb = (hex) => {
  const rgb = hexToRgbGuide(hex);
  return [rgb.r / 255, rgb.g / 255, rgb.b / 255];
};

const hexChannelToString = (channel) =>
  Math.round(channel).toString(16).padStart(2, "0").toUpperCase();

const createRimHex = (hex) => {
  const rgb = hexToRgbGuide(hex);
  const tintAmount = 0.72;
  const r = rgb.r + (255 - rgb.r) * tintAmount;
  const g = rgb.g + (255 - rgb.g) * tintAmount;
  const b = rgb.b + (255 - rgb.b) * tintAmount;

  return `#${hexChannelToString(r)}${hexChannelToString(g)}${hexChannelToString(b)}`;
};

const smoothstep = (value) => value * value * (3 - 2 * value);

const TALKING_AUDIO_INPUT_GAIN = 1.8;
const TALKING_AUDIO_FLOOR = 0.006;
const TALKING_AUDIO_CONTRAST_EXPONENT = 0.95;
const TALKING_AUDIO_PEAK_GAIN = 1.9;

const easeAudioActivityLevel = (state, value) => {
  const positiveValue = getPositiveAudioLevel(value);
  if (state === "TALKING") {
    const expandedValue =
      Math.max(0, positiveValue - TALKING_AUDIO_FLOOR) /
      (1 - TALKING_AUDIO_FLOOR);
    const boostedValue = expandedValue * TALKING_AUDIO_INPUT_GAIN;
    const contrastedValue =
      Math.pow(boostedValue, TALKING_AUDIO_CONTRAST_EXPONENT) *
      TALKING_AUDIO_PEAK_GAIN;
    return contrastedValue <= 1
      ? contrastedValue
      : 1 + Math.log1p(contrastedValue - 1) * 0.7;
  }

  if (positiveValue <= 1) return smoothstep(positiveValue);
  return 1 + Math.log1p(positiveValue - 1) * 0.7;
};

const getVisualStateConfig = (state) => {
  const defaultConfig = GUIDE_VISUAL_STATES.DEFAULT;
  const stateConfig = GUIDE_VISUAL_STATES[state] || defaultConfig;
  const radius = stateConfig.radius ?? defaultConfig.radius;
  const radiusScale = radius / defaultConfig.radius;

  return {
    ...defaultConfig,
    ...stateConfig,
    rimHex: stateConfig.rimHex || createRimHex(stateConfig.hex || defaultConfig.hex),
    height: stateConfig.height ?? radius,
    depth: stateConfig.depth ?? defaultConfig.depth * radiusScale,
  };
};

const createTargetValues = (state, audioLevel) => {
  const config = getVisualStateConfig(state);
  const audioDriven = state === "TALKING" || state === "LISTENING";
  const inputLevel = audioDriven ? getPositiveAudioLevel(audioLevel) : 0;
  const activityLevel = audioDriven ? inputLevel : config.internalLevel;
  const easedLevel = easeAudioActivityLevel(state, activityLevel);
  const activeRadius =
    Math.max(config.radius, config.height) +
    easedLevel * Math.max(config.volumeRadius, config.volumeHeight);

  return {
    baseColor: hexToUnitRgb(config.hex),
    rimColor: hexToUnitRgb(config.rimHex),
    size: [
      activeRadius,
      activeRadius,
      config.depth + easedLevel * 0.055,
    ],
    rimPower: Math.max(1.2, config.rimPower - easedLevel * 0.28),
    rimEmitBoost: config.rimEmitBoost + easedLevel * 2.1,
    amplitude: config.amplitude + easedLevel * config.volumeAmplitude,
    frequency: config.frequency + easedLevel * config.volumeFrequency,
    speed: config.speed + easedLevel * config.volumeSpeed,
    blend: 1,
    alpha: Math.min(0.92, config.alpha + easedLevel * 0.07),
    audioLevel: easedLevel,
  };
};

const createInitialValues = () => createTargetValues("DEFAULT", 0);

const lerpNumber = (from, to, amount) => from + (to - from) * amount;

const lerpArray = (from, to, amount) =>
  from.map((value, index) => lerpNumber(value, to[index], amount));

const easedAmount = (deltaSeconds, responseSeconds) => {
  const safeDelta = Math.max(0, Math.min(deltaSeconds, 0.1));
  return 1 - Math.exp(-safeDelta / responseSeconds);
};

const compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const error = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(error || "Failed to compile guide waveform shader.");
  }

  return shader;
};

const createProgram = (gl) => {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const error = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(error || "Failed to link guide waveform shader.");
  }

  return program;
};

const getUniformLocations = (gl, program) => ({
  time: gl.getUniformLocation(program, "uTime"),
  size: gl.getUniformLocation(program, "uSize"),
  baseColor: gl.getUniformLocation(program, "uBaseColor"),
  rimColor: gl.getUniformLocation(program, "uRimColor"),
  rimPower: gl.getUniformLocation(program, "uRimPower"),
  rimEmitBoost: gl.getUniformLocation(program, "uRimEmitBoost"),
  defAmplitude: gl.getUniformLocation(program, "uDefAmplitude"),
  defFrequency: gl.getUniformLocation(program, "uDefFrequency"),
  defBlend: gl.getUniformLocation(program, "uDefBlend"),
  phase: gl.getUniformLocation(program, "uPhase"),
  audioLevel: gl.getUniformLocation(program, "uAudioLevel"),
  alpha: gl.getUniformLocation(program, "uAlpha"),
});

const updateCurrentValues = (current, target, deltaSeconds) => {
  const stateAmount = easedAmount(deltaSeconds, 0.78);
  const radiusAmount = easedAmount(deltaSeconds, 0.32);
  const audioMotionAmount = easedAmount(deltaSeconds, 0.1);
  const audioAmount = easedAmount(deltaSeconds, 0.045);

  return {
    baseColor: lerpArray(current.baseColor, target.baseColor, stateAmount),
    rimColor: lerpArray(current.rimColor, target.rimColor, stateAmount),
    size: lerpArray(current.size, target.size, radiusAmount),
    rimPower: lerpNumber(current.rimPower, target.rimPower, stateAmount),
    rimEmitBoost: lerpNumber(current.rimEmitBoost, target.rimEmitBoost, stateAmount),
    amplitude: lerpNumber(current.amplitude, target.amplitude, audioMotionAmount),
    frequency: lerpNumber(current.frequency, target.frequency, audioMotionAmount),
    speed: lerpNumber(current.speed, target.speed, audioMotionAmount),
    blend: lerpNumber(current.blend, target.blend, stateAmount),
    alpha: lerpNumber(current.alpha, target.alpha, stateAmount),
    audioLevel: lerpNumber(current.audioLevel, target.audioLevel, audioAmount),
  };
};

const setCanvasSize = (canvas, gl, size) => {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pixelSize = Math.max(1, Math.round(size * dpr));

  if (canvas.width !== pixelSize || canvas.height !== pixelSize) {
    canvas.width = pixelSize;
    canvas.height = pixelSize;
  }

  gl.viewport(0, 0, pixelSize, pixelSize);
};

const getNow = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const canAttemptWebGl = () =>
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  typeof window.WebGLRenderingContext !== "undefined";

const GuideWaveformOrbFallback = ({ state, size }) => {
  const config = getVisualStateConfig(state);

  return (
    <div
      data-testid="guide-waveform-orb-fallback"
      style={{
        width: size,
        height: size,
        borderRadius: "46% 54% 49% 51%",
        background: `radial-gradient(circle at 32% 24%, ${config.rimHex} 0%, ${config.hex} 46%, rgba(255,255,255,0.2) 100%)`,
        boxShadow: `0 0 16px ${config.hex}55, inset 0 0 18px rgba(255,255,255,0.35)`,
        animation: "guideOrbFallbackDrift 2200ms ease-in-out infinite alternate",
      }}
    />
  );
};

const GuideWaveformOrb = ({ state, audioLevel = 0, size = 160 }) => {
  const canvasRef = useRef(null);
  const glStateRef = useRef(null);
  const rafRef = useRef(null);
  const currentRef = useRef(createInitialValues());
  const targetRef = useRef(createTargetValues(state, audioLevel));
  const [webGlUnavailable, setWebGlUnavailable] = useState(false);

  const positiveAudioLevel = getPositiveAudioLevel(audioLevel);
  const targetValues = useMemo(
    () => createTargetValues(state, positiveAudioLevel),
    [positiveAudioLevel, state],
  );

  useEffect(() => {
    targetRef.current = targetValues;
  }, [targetValues]);

  useEffect(() => {
    if (!canAttemptWebGl()) {
      setWebGlUnavailable(true);
      return undefined;
    }

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const contextAttributes = {
      alpha: true,
      antialias: true,
      depth: true,
      premultipliedAlpha: false,
    };
    const gl =
      canvas.getContext("webgl", contextAttributes) ||
      canvas.getContext("experimental-webgl", contextAttributes);

    if (
      !gl ||
      typeof gl.createShader !== "function" ||
      typeof gl.createProgram !== "function"
    ) {
      setWebGlUnavailable(true);
      return undefined;
    }

    let program;
    let positionBuffer;
    let normalBuffer;
    let indexBuffer;

    try {
      program = createProgram(gl);
      positionBuffer = gl.createBuffer();
      normalBuffer = gl.createBuffer();
      indexBuffer = gl.createBuffer();
    } catch (error) {
      console.warn("Guide waveform WebGL setup failed:", error);
      setWebGlUnavailable(true);
      return undefined;
    }

    const positionLocation = gl.getAttribLocation(program, "aPosition");
    const normalLocation = gl.getAttribLocation(program, "aNormal");
    const uniforms = getUniformLocations(gl, program);

    glStateRef.current = {
      gl,
      program,
      positionBuffer,
      normalBuffer,
      indexBuffer,
      uniforms,
    };

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, ORB_GEOMETRY.positions, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, ORB_GEOMETRY.normals, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(normalLocation);
    gl.vertexAttribPointer(normalLocation, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, ORB_GEOMETRY.indices, gl.STATIC_DRAW);

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(true);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.frontFace(gl.CCW);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(
      gl.SRC_ALPHA,
      gl.ONE_MINUS_SRC_ALPHA,
      gl.ONE,
      gl.ONE_MINUS_SRC_ALPHA,
    );
    gl.clearColor(0, 0, 0, 0);
    gl.clearDepth(1);
    setCanvasSize(canvas, gl, size);

    const startTime = getNow();
    let previousFrameTime = startTime;
    let phase = 0;

    const render = () => {
      const frameTime = getNow();
      const deltaSeconds = (frameTime - previousFrameTime) / 1000;
      previousFrameTime = frameTime;
      const current = updateCurrentValues(
        currentRef.current,
        targetRef.current,
        deltaSeconds,
      );
      currentRef.current = current;
      phase += current.speed * deltaSeconds;

      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
      gl.vertexAttribPointer(normalLocation, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
      gl.uniform1f(uniforms.time, (frameTime - startTime) / 1000);
      gl.uniform3fv(uniforms.size, current.size);
      gl.uniform3fv(uniforms.baseColor, current.baseColor);
      gl.uniform3fv(uniforms.rimColor, current.rimColor);
      gl.uniform1f(uniforms.rimPower, current.rimPower);
      gl.uniform1f(uniforms.rimEmitBoost, current.rimEmitBoost);
      gl.uniform1f(uniforms.defAmplitude, current.amplitude);
      gl.uniform1f(uniforms.defFrequency, current.frequency);
      gl.uniform1f(uniforms.defBlend, current.blend);
      gl.uniform1f(uniforms.phase, phase);
      gl.uniform1f(uniforms.audioLevel, current.audioLevel);
      gl.uniform1f(uniforms.alpha, current.alpha);
      gl.drawElements(gl.TRIANGLES, ORB_GEOMETRY.indexCount, gl.UNSIGNED_SHORT, 0);

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      gl.deleteBuffer(positionBuffer);
      gl.deleteBuffer(normalBuffer);
      gl.deleteBuffer(indexBuffer);
      gl.deleteProgram(program);
      glStateRef.current = null;
    };
  }, [size]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = glStateRef.current?.gl;
    if (!canvas || !gl) return;

    setCanvasSize(canvas, gl, size);
  }, [size]);

  if (webGlUnavailable) {
    return <GuideWaveformOrbFallback state={state} size={size} />;
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid="guide-waveform-orb"
      style={{
        width: size,
        height: size,
        display: "block",
        overflow: "visible",
      }}
    />
  );
};

export default GuideWaveformOrb;
